import { validateEmail } from '@/lib/auth/constants';
import type { FieldConfig } from '@/lib/base/forms/hooks/useFormErrors';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';

/**
 * Validate a single phone value (either a structured PhoneNumber object or a
 * string). Called once for scalar phone fields and once per item for is_multi
 * fields (e.g. ahoy_phone_number).
 */
function validatePhoneScalar(
  value: unknown,
  required: boolean
): string | undefined {
  // PhoneInput passes structured { country_code, national_number } objects
  if (
    typeof value === 'object' &&
    value !== null &&
    'national_number' in value
  ) {
    const phone = value as Record<string, unknown>;
    const num =
      typeof phone.national_number === 'string'
        ? phone.national_number.trim()
        : '';
    if (!num) {
      return required ? 'Required' : undefined;
    }
    if (!/^\d[\d\s\-().]*$/.test(num)) {
      return 'Invalid phone number';
    }
    return undefined;
  }
  const str = String(value).trim();
  if (!/^(?=.*\d)[+\d(][\d\s\-().]+$/.test(str)) {
    return 'Invalid phone number';
  }
  return undefined;
}

/**
 * Derives FieldConfig objects from CRM property schemas at runtime.
 *
 * Validators return `undefined` for empty/null/undefined values so that
 * `useFormErrors` does not report validation errors on optional empty fields.
 *
 * @param schemas - Map of property_type_name → PropertyTypeSchema
 * @param properties - List of property_type_names to include in the config
 * @returns Map of property_type_name → FieldConfig
 */
export function buildFieldConfigFromSchema(
  schemas: Record<string, PropertyTypeSchema>,
  properties: string[]
): Record<string, FieldConfig> {
  const result: Record<string, FieldConfig> = {};

  for (const prop of properties) {
    const schema = schemas[prop];
    if (!schema) {
      continue;
    }

    const config: FieldConfig = {
      required: schema.is_required,
    };

    switch (schema.field_type_name) {
      case 'email':
        config.validate = (value: unknown): string | undefined =>
          validateEmail(value);
        break;

      case 'url':
        config.validate = (value: unknown): string | undefined => {
          if (
            value === undefined ||
            value === null ||
            value === '' ||
            (typeof value === 'string' && !value.trim())
          ) {
            return undefined;
          }
          if (typeof value !== 'string') {
            return 'Invalid URL';
          }
          const str = value.trim();
          try {
            const parsed = new URL(str);
            if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
              return 'Invalid URL';
            }
          } catch {
            return 'Invalid URL';
          }
          return undefined;
        };
        break;

      case 'phone_number':
        config.validate = (value: unknown): string | undefined => {
          if (
            value === undefined ||
            value === null ||
            value === '' ||
            (typeof value === 'string' && !value.trim())
          ) {
            return undefined;
          }
          // Multi-value fields (e.g. ahoy_phone_number is is_multi=true) store
          // values as arrays of PhoneNumber objects. Validate each entry.
          if (Array.isArray(value)) {
            if (value.length === 0) {
              return config.required ? 'Required' : undefined;
            }
            for (const item of value) {
              const err = validatePhoneScalar(item, !!config.required);
              if (err) {
                return err;
              }
            }
            return undefined;
          }
          return validatePhoneScalar(value, !!config.required);
        };
        break;

      case 'number':
        config.validate = (value: unknown): string | undefined => {
          if (
            value === undefined ||
            value === null ||
            value === '' ||
            (typeof value === 'string' && !value.trim())
          ) {
            return undefined;
          }
          if (typeof value !== 'string' && typeof value !== 'number') {
            return 'Invalid number';
          }
          const num = Number(value);
          if (!Number.isFinite(num)) {
            return 'Invalid number';
          }
          return undefined;
        };
        break;

      default:
        break;
    }

    result[prop] = config;
  }

  return result;
}
